import React, { useMemo } from "react";
import { Row, Col, Card, Placeholder } from "react-bootstrap";
import { FaArrowUp, FaArrowDown, FaMinus } from "react-icons/fa";
import { COST_BASIS, costBasisLabel, dailyCostOf, monthlyCostOf } from "./costBasis";

// Same fixed neutral accent as the Usage Insights cards (see UsageInsights.jsx).
const NEUTRAL_ACCENT = "#007681";

function AzureCostInsights({ dailyData, monthlyData, loading, columns = 4 }) {
  // Daily figures are always Amortized, monthly always Actual — see costBasis.js.
  const basisLabel = costBasisLabel(COST_BASIS.AMORTIZED);
  const insights = useMemo(() => {
    if (!dailyData || dailyData.length === 0) return null;

    // Group by date and sum costs
    const byDate = {};
    dailyData.forEach((item) => {
      const date = item.costDate?.split("T")[0] || item.costDate;
      if (!byDate[date]) byDate[date] = 0;
      byDate[date] += dailyCostOf(item);
    });

    const sortedDays = Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b));

    if (sortedDays.length === 0) return null;

    // Last 30 days
    const last30 = sortedDays.slice(-30);
    const prior30 = sortedDays.slice(-60, -30);

    const last30Total = last30.reduce((sum, [, cost]) => sum + cost, 0);
    const prior30Total = prior30.reduce((sum, [, cost]) => sum + cost, 0);
    const avgDaily = last30Total / (last30.length || 1);

    // Month-over-month change
    let momChange = null;
    if (prior30Total > 0) {
      momChange = ((last30Total - prior30Total) / prior30Total) * 100;
    }

    // Monthly totals for MoM comparison
    const monthlyTotals = {};
    if (monthlyData && monthlyData.length > 0) {
      monthlyData.forEach((item) => {
        const key = `${item.year}-${String(item.month).padStart(2, "0")}`;
        if (!monthlyTotals[key]) monthlyTotals[key] = 0;
        monthlyTotals[key] += monthlyCostOf(item);
      });
    }
    const sortedMonths = Object.entries(monthlyTotals).sort(([a], [b]) => a.localeCompare(b));

    // Skip the current (incomplete) month — compare last full month vs the one before
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const fullMonths = sortedMonths.filter(([key]) => key !== currentMonthKey);

    const lastMonth = fullMonths.length >= 1 ? fullMonths[fullMonths.length - 1] : null;
    const prevMonth = fullMonths.length >= 2 ? fullMonths[fullMonths.length - 2] : null;

    // Subscription breakdown for growth (using full months only)
    const subByMonth = {};
    if (monthlyData && monthlyData.length > 0) {
      monthlyData.forEach((item) => {
        const key = `${item.year}-${String(item.month).padStart(2, "0")}`;
        if (!subByMonth[item.subscriptionName]) subByMonth[item.subscriptionName] = {};
        if (!subByMonth[item.subscriptionName][key]) subByMonth[item.subscriptionName][key] = 0;
        subByMonth[item.subscriptionName][key] += monthlyCostOf(item);
      });
    }

    // Find fastest growing subscription (last full month vs prior full month)
    let fastestGrowing = null;
    if (lastMonth && prevMonth) {
      let maxGrowth = -Infinity;
      Object.entries(subByMonth).forEach(([sub, months]) => {
        const curr = months[lastMonth[0]] || 0;
        const prev = months[prevMonth[0]] || 0;
        if (prev > 0) {
          const growth = ((curr - prev) / prev) * 100;
          if (growth > maxGrowth) {
            maxGrowth = growth;
            fastestGrowing = { name: sub, growth, current: curr, previous: prev };
          }
        }
      });
    }

    return {
      last30Total,
      avgDaily,
      momChange,
      fastestGrowing,
    };
  }, [dailyData, monthlyData]);

  if (loading) {
    return (
      <Row className="g-3 mb-3">
        {[1, 2].map((i) => (
          <Col md={columns} key={i}>
            <Card>
              <Card.Body>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={6} size="sm" />
                  <Placeholder xs={8} size="lg" className="mt-2" />
                </Placeholder>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  if (!insights) return null;

  const formatCurrency = (val) =>
    `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const TrendIndicator = ({ value, invertColor = false }) => {
    if (value === null || value === undefined) return <span className="text-muted">N/A</span>;
    const isUp = value > 0;
    const isFlat = Math.abs(value) < 0.5;
    const color = isFlat ? "text-muted" : (isUp ? (invertColor ? "text-success" : "text-danger") : (invertColor ? "text-danger" : "text-success"));
    const Icon = isFlat ? FaMinus : isUp ? FaArrowUp : FaArrowDown;
    return (
      <span className={color}>
        <Icon className="me-1" size={12} />
        {Math.abs(value).toFixed(1)}%
      </span>
    );
  };

  return (
    <Row className="g-3 mb-3">
      <Col md={columns}>
        <Card className="h-100 border-start border-4" style={{ borderLeftColor: NEUTRAL_ACCENT }}>
          <Card.Body className="py-3">
            <small className="text-muted fw-bold text-uppercase">Last 30 Days ({basisLabel})</small>
            <h3 className="mb-1 mt-1">{formatCurrency(insights.last30Total)}</h3>
            <small>
              <TrendIndicator value={insights.momChange} /> vs prior 30 days
            </small>
          </Card.Body>
        </Card>
      </Col>

      <Col md={columns}>
        <Card className="h-100 border-start border-4" style={{ borderLeftColor: NEUTRAL_ACCENT }}>
          <Card.Body className="py-3">
            <small className="text-muted fw-bold text-uppercase">Avg Daily Cost ({basisLabel})</small>
            <h3 className="mb-1 mt-1">{formatCurrency(insights.avgDaily)}</h3>
            <small className="text-muted">Based on last 30 days</small>
          </Card.Body>
        </Card>
      </Col>

      {insights.fastestGrowing && Math.abs(insights.fastestGrowing.growth) > 1 && (
        <Col xs={12}>
          <div className="bg-light rounded p-2 px-3">
            <small>
              <strong>Subscription Trend:</strong>{" "}
              <span className="fw-bold">{insights.fastestGrowing.name}</span> had the largest change at{" "}
              <TrendIndicator value={insights.fastestGrowing.growth} />
              {" "}({formatCurrency(insights.fastestGrowing.previous)} → {formatCurrency(insights.fastestGrowing.current)})
            </small>
          </div>
        </Col>
      )}
    </Row>
  );
}

export default AzureCostInsights;
